import React, { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { NavigationTab, ROUTES, APP_CONFIG } from '@/lib/constants';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useDashboard } from '@/hooks/useDashboardQueries';
import { useAuth } from '@/services/auth/authContext';
import { AuthLoadingScreen } from '@/features/auth/AuthLoadingScreen';
import { LoginView } from '@/features/auth/LoginView';
import { OverviewView } from '@/features/overview/OverviewView';
import { PositionsView } from '@/features/positions/PositionsView';
import { TradesView } from '@/features/trades/TradesView';
import { OrdersView } from '@/features/orders/OrdersView';
import { HistoryView } from '@/features/history/HistoryView';
import { SignalsView } from '@/features/signals/SignalsView';
import { RiskHealthView } from '@/features/risk-health/RiskHealthView';
import { AnalyticsView } from '@/features/analytics/AnalyticsView';
import { ThemesView } from '@/features/themes/ThemesView';
import { SettingsView } from '@/features/settings/SettingsView';

const AuthenticatedApp: React.FC = () => {
  const [activeTab, setActiveTab] = useState<NavigationTab>(ROUTES.OVERVIEW);
  const [isMockMode, setIsMockModeState] = useState<boolean>(APP_CONFIG.enableMockFallback);

  // WebSocket Manager Hook (connects when mounted in authenticated state)
  const {
    status: wsStatus,
    reconnectAttempt: wsReconnectAttempt,
    setMockMode,
    reconnect: wsReconnect,
  } = useWebSocket();

  // Top-level status query (uses REST polling fallback when WS is not LIVE)
  const { data: dashboardData, isFetching: isRefreshing, refetch } = useDashboard({
    refetchInterval: wsStatus === 'LIVE' ? 30000 : 5000,
  });

  const handleToggleMockMode = () => {
    const nextState = !isMockMode;
    setIsMockModeState(nextState);
    setMockMode(nextState);
  };

  // Render view by active tab
  const renderActiveView = () => {
    switch (activeTab) {
      case ROUTES.OVERVIEW:
        return <OverviewView onNavigateTab={setActiveTab} />;
      case ROUTES.POSITIONS:
        return <PositionsView />;
      case ROUTES.TRADES:
        return <TradesView />;
      case ROUTES.ORDERS:
        return <OrdersView />;
      case ROUTES.HISTORY:
        return <HistoryView />;
      case ROUTES.SIGNALS:
        return <SignalsView />;
      case ROUTES.RISK_HEALTH:
        return <RiskHealthView />;
      case ROUTES.ANALYTICS:
        return <AnalyticsView />;
      case ROUTES.THEMES:
        return <ThemesView />;
      case ROUTES.SETTINGS:
        return <SettingsView />;
      default:
        return <OverviewView onNavigateTab={setActiveTab} />;
    }
  };

  const botStatus = dashboardData?.bot_status;
  const positionsSummary = dashboardData?.positions_summary;

  return (
    <AppLayout
      activeTab={activeTab}
      onSelectTab={setActiveTab}
      wsStatus={wsStatus}
      wsReconnectAttempt={wsReconnectAttempt}
      marketStatus={botStatus?.market_status || 'OPEN'}
      botStatus={botStatus?.status || 'RUNNING'}
      paperMode={botStatus?.mode !== 'LIVE'}
      lastCycle={botStatus?.last_cycle}
      lastSignal={botStatus?.last_signal}
      openPositionsCount={positionsSummary?.open_positions_count || 0}
      unacknowledgedSignalsCount={0}
      lastSyncTime={new Date().toISOString()}
      uptime={botStatus?.uptime || '—'}
      isMockMode={isMockMode}
      onToggleMockMode={handleToggleMockMode}
      onRefresh={() => {
        refetch();
        if (wsStatus === 'OFFLINE') wsReconnect();
      }}
      isRefreshing={isRefreshing}
    >
      {renderActiveView()}
    </AppLayout>
  );
};

export const App: React.FC = () => {
  const { status: authStatus } = useAuth();

  // Handle Authentication Gate
  if (authStatus === 'INITIAL_CHECK' || authStatus === 'OFFLINE_ERROR') {
    return <AuthLoadingScreen />;
  }

  if (authStatus === 'UNAUTHENTICATED') {
    return <LoginView />;
  }

  return <AuthenticatedApp />;
};
